import React, { useState, useRef } from 'react';

// Styles & Assets
import styles from './DialogFilterPolls.module.css';
import { FaRegTrashAlt } from 'react-icons/fa';

// Hooks
import { useTranslation } from 'react-i18next';

// Components
import CancelConfirmButtons from '../../UI/CancelConfirmButtons/CancelConfirmButtons';
import CheckboxButton from '../../UI/CheckboxButton/CheckboxButton';
import { InquiryTypes } from '../../../services/Poll';
import SliderCheckbox from '../../UI/SliderCheckbox/SliderCheckbox';
import HourSelector from '../../UI/HourSelector/HourSelector';

// Other
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker/DatePicker';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';

// Interfaces
import { HH, mm } from '../../../interfaces/Time';
interface IHourSelector {
    hours: HH
    minutes: mm
}
interface Props {
    defaultTypePoll: InquiryTypes[]
    defaultSeeAnswered: boolean
    defaultFromDate: moment.Moment | null
    defaultToDate: moment.Moment | null
    closeModal: () => void
    onConfirm: (type: InquiryTypes[], seeOnlyAnswered: boolean, fromDate: moment.Moment | null, toDate: moment.Moment | null) => void
}

const DialogFilterPolls: React.FC<Props> = ({ defaultTypePoll, defaultSeeAnswered, defaultFromDate, defaultToDate, closeModal, onConfirm }) => {

    const { t } = useTranslation();

    const handleAddType = (type: InquiryTypes) => (checked: boolean) => {
        typePoll.current = checked ?
            [...typePoll.current, type]
            :
            typePoll.current.filter(t => t !== type);
    };
    const typePoll = useRef<InquiryTypes[]>(defaultTypePoll ?? []);

    const [onlyAnswered, setOnlyAnswered] = useState(defaultSeeAnswered ?? false);

    const fromDate = useRef<moment.Moment | null>(defaultFromDate ? moment(defaultFromDate.clone().format('DD-MM-YYYY HH:mm'), 'DD-MM-YYYY HH:mm') : null);
    const toDate = useRef<moment.Moment | null>(defaultToDate ? moment(defaultToDate.clone().format('DD-MM-YYYY HH:mm'), 'DD-MM-YYYY HH:mm') : null);

    const addZeroToLeft = (value: number) => (value < 10 ? '0' : '') + value.toString();

    const defaultFromHour = defaultFromDate ?
        { hours: addZeroToLeft(defaultFromDate.hours()) as HH, minutes: addZeroToLeft(defaultFromDate.minutes()) as mm }
        : null;
    const defaultToHour = defaultToDate ?
        { hours: addZeroToLeft(defaultToDate.hours()) as HH, minutes: addZeroToLeft(defaultToDate.minutes()) as mm }
        : null;

    const [fromHour, setFromHour] = useState<IHourSelector | null>(defaultFromHour);
    const [toHour, setToHour] = useState<IHourSelector | null>(defaultToHour);

    const handleChangeFromHours = (hours: HH | null, minutes: mm | null) => {
        if (hours === null || minutes === null) return;
        setFromHour({ hours, minutes });
    };

    const handleChangeToHours = (hours: HH | null, minutes: mm | null) => {
        if (hours === null || minutes === null) return;
        setToHour({ hours, minutes });
    };

    const getFullDate = (currentDate: moment.Moment | null, currentHour: IHourSelector | null) => {
        const date = currentDate === null ? null : currentDate.clone();
        if (date !== null && currentHour !== null) {
            date.hours(Number(currentHour.hours));
            date.minutes(Number(currentHour.minutes));
        }
        return date;
    };

    const deleteFilters = () => {
        onConfirm([], false, null, null);
        closeModal();
    };

    const handleConfirm = () => {
        const from = getFullDate(fromDate.current, fromHour);
        const to = getFullDate(toDate.current, toHour);
        onConfirm(typePoll.current, onlyAnswered, from, to);
        closeModal();
    };

    return (
        <div className='dialog'>
            <div className={styles.headerDialog}>
                <h1 className='dialogTitle'>{t('filter')}</h1>
                <button onClick={deleteFilters} className='buttonRemoveFilter'>
                    {t('remove_filters')} <FaRegTrashAlt />
                </button>
            </div>
            {window.config.DIFFERENCE_TESTPROM_POLLS &&
                <>
                    <h2 className={styles.subtitle}>{t('type_poll')}</h2>
                    <div className={styles.checkboxesContainer}>
                        <CheckboxButton
                            defaultChecked={defaultTypePoll.includes(InquiryTypes.TEST)}
                            label={InquiryTypes.TEST}
                            onClick={handleAddType(InquiryTypes.TEST)}
                        />
                        <CheckboxButton
                            defaultChecked={defaultTypePoll.includes(InquiryTypes.PROM)}
                            label={InquiryTypes.PROM}
                            onClick={handleAddType(InquiryTypes.PROM)}
                        />
                    </div>
                </>
            }
            <div className={styles.sliderContainer}>
                <h2 className={styles.subtitle} style={{ margin: 0 }}>{t('only_answered_polls')}</h2>
                <SliderCheckbox
                    checked={onlyAnswered}
                    handleChange={() => setOnlyAnswered(x => !x)}
                    label=''
                />
            </div>
            <h2 className={styles.subtitle}>{t('date_poll')}</h2>
            <h3 className={styles.thirdTitle}>
                {t('from')}:
            </h3>
            <div className={styles.dateContainer}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                        slotProps={{
                            textField: { size: 'small' }
                        }}
                        defaultValue={defaultFromDate}
                        onChange={(val: moment.Moment | null) => { fromDate.current = val; }}
                    />
                </LocalizationProvider>
                <HourSelector
                    hours={fromHour?.hours ?? null}
                    minutes={fromHour?.minutes ?? null}
                    handleChange={handleChangeFromHours}
                />
            </div>
            <h3 className={styles.thirdTitle}>
                {t('to')}:
            </h3>
            <div className={styles.dateContainer}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                        slotProps={{
                            textField: { size: 'small' }
                        }}
                        defaultValue={defaultToDate}
                        onChange={(val: moment.Moment | null) => { toDate.current = val; }}
                    />
                </LocalizationProvider>
                <HourSelector
                    hours={toHour?.hours ?? null}
                    minutes={toHour?.minutes ?? null}
                    handleChange={handleChangeToHours}
                />
            </div>
            <CancelConfirmButtons
                onCancel={closeModal}
                onConfirm={handleConfirm}
                confirmName={t('filter') as string}
            />
        </div>
    );
};

export default DialogFilterPolls;